import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Modal } from '../../../components/Modal';

/**
 * Non-negotiable #2 in general form: the action stays disabled until a reason is
 * written. Used for hold, cancel, reject and send back.
 */
export function ReasonDialog({
  title,
  description,
  label,
  confirmLabel,
  isDanger = false,
  isPending,
  isReasonOptional = false,
  onConfirm,
  onClose,
}: {
  readonly title: string;
  readonly description: string;
  readonly label: string;
  readonly confirmLabel: string;
  readonly isDanger?: boolean;
  readonly isPending: boolean;
  /** The owner withdrawing their own draft needs no reason (§L25). */
  readonly isReasonOptional?: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}): ReactNode {
  const [reason, setReason] = useState('');
  const isReady = isReasonOptional || reason.trim() !== '';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant={isDanger ? 'danger' : 'solid'}
            disabled={!isReady}
            isPending={isPending}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Field label={label} isRequired={!isReasonOptional} isOptional={isReasonOptional}>
        {({ inputId }) => (
          <Textarea
            id={inputId}
            rows={3}
            autoFocus
            maxLength={2000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && isReady) {
                event.preventDefault();
                onConfirm(reason.trim());
              }
            }}
          />
        )}
      </Field>
    </Modal>
  );
}
